'use client';

import { useCerFeatures } from '@/hooks/use-cer-features';
import { isFeedLive } from '@slims/shared/types';

import {
	DashboardConfigCard,
	type DashboardConfigCardData,
	DashboardConfigCardSkeleton,
	DashboardPageView,
	DashboardPanelView,
	type DashboardViewPeriod,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { useMemo, useState } from 'react';
import { MetricExplode, type MetricExplodeTarget } from '@/components/energy/metric-explode';
import { useAiActionsCount } from '@/hooks/use-ai-actions';
import { rangeForPeriod, useDashboardSummary } from '@/hooks/use-dashboard-summary';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useEnergyMembers } from '@/hooks/use-energy-members';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { useConfigToggle } from '@/hooks/use-config-toggle';

/** Giorno civile locale `YYYY-MM-DD` di un istante ISO. */
function civilDay(iso: string): string {
	const d = new Date(iso);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Ultimo giorno civile **incluso** nella finestra.
 *
 * `to` è un istante di fine, e su «ieri» e «ultima validazione» cade sulla
 * mezzanotte del giorno dopo: preso com'è, l'esploso guarderebbe un giorno più
 * del pannello — la classifica sommerebbe anche oggi, e la curva per POD si
 * porterebbe dietro una coda che nel grafico da cui si è aperta non c'è.
 */
function lastCivilDay(iso: string): string {
	const end = new Date(iso);
	const midnight =
		end.getHours() === 0 &&
		end.getMinutes() === 0 &&
		end.getSeconds() === 0 &&
		end.getMilliseconds() === 0;
	return civilDay(midnight ? new Date(end.getTime() - 1).toISOString() : iso);
}

/**
 * La finestra su cui aprire l'esploso: la stessa del KPI cliccato, o il piede di
 * quadratura direbbe che i due numeri non tornano quando invece guardano periodi
 * diversi.
 */
function explodeRange(period: DashboardViewPeriod): { from: string; to: string } {
	const range = rangeForPeriod(period);
	const from = civilDay(range.dayFrom);
	const to = lastCivilDay(range.to);
	// A cavallo esatto della mezzanotte «oggi» avrebbe `to` un giorno prima di
	// `from`: la finestra collassa sul giorno di partenza invece di invertirsi.
	return { from, to: to < from ? from : to };
}

/**
 * Come le altre pagine (MySlims/Report/Incentivi), la Dashboard guarda solo le
 * configurazioni scelte nel selettore CER/configurazioni dell'header: la
 * griglia «Panoramica» e il picker della modalità singola mostrano quelle, non
 * tutte le raggiungibili. Il toggle Panoramica/Configurazione-singola resta suo
 * e decide *come* guardarle — quali, lo decide la navbar.
 */
export default function DashboardPage() {
	const { leaves } = useScopeTree();
	const {
		scopes,
		loading: scopesLoading,
		view,
		setView,
		configs,
		effectiveLeaf,
		openConfig,
	} = useConfigToggle();
	const [multiPeriod, setMultiPeriod] = useState<DashboardViewPeriod>('oggi');

	if (!effectiveLeaf) {
		return (
			<DashboardPageView
				content={
					<StatusBlock
						kind={scopesLoading ? 'loading' : 'empty'}
						size="lg"
						message={
							scopesLoading
								? 'Caricamento...'
								: leaves.length > 0
									? 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
									: 'Nessuna configurazione disponibile. Contatta un amministratore.'
						}
					/>
				}
			/>
		);
	}

	return (
		<DashboardPageView
			view={view}
			onViewChange={setView}
			configs={configs}
			selectedConfigId={effectiveLeaf.orgId}
			onSelectConfig={openConfig}
			multiPeriod={multiPeriod}
			onMultiPeriodChange={setMultiPeriod}
			gridSlot={
				<>
					{scopes.map((leaf) => (
						<DashboardConfigCardWiring
							key={leaf.orgId}
							leaf={leaf}
							period={multiPeriod}
							onOpen={() => openConfig(leaf.orgId)}
						/>
					))}
				</>
			}
			content={<DashboardPanelWiring orgId={effectiveLeaf.orgId} />}
		/>
	);
}

/** Alimenta una card della griglia «Panoramica»: le tre grandezze del periodo scelto, per una configurazione sola. */
function DashboardConfigCardWiring({
	leaf,
	period,
	onOpen,
}: {
	leaf: ScopeLeaf;
	period: DashboardViewPeriod;
	onOpen: () => void;
}) {
	// Nome e dati energetici sono leggibili da ogni membership sulla configurazione.
	const { config } = useScopedConfig(leaf.orgId);
	const { summary, isLoading: summaryLoading } = useDashboardSummary(leaf.orgId, period);
	const periodTotals = summary?.period ?? null;

	// Come nel pannello singolo (WB-R17): finché il fetch non torna, uno
	// skeleton della stessa forma — non zeri che si leggerebbero come un
	// impianto fermo invece che come un dato ancora in arrivo.
	if (summaryLoading) {
		return <DashboardConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const data: DashboardConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		prodKwh: Math.round(periodTotals?.productionKwh ?? 0),
		condKwh: Math.round(periodTotals?.sharedKwh ?? 0),
		incKwh: Math.round(periodTotals?.incentivizedKwh ?? 0),
		live: isFeedLive(summary?.ultimaLetturaAt),
	};

	return <DashboardConfigCard data={data} onClick={onOpen} />;
}

function DashboardPanelWiring({ orgId }: { orgId: string }) {
	const { features } = useCerFeatures(orgId);
	const { config, loading: configLoading } = useScopedConfig(orgId);
	const { members, isLoading: membersLoading } = useEnergyMembers(orgId);
	const { totals: energyTotals, isLoading: energyLoading } = useEnergyData(undefined, orgId);
	// La finestra la sceglie la vista; qui si rifà la fetch. `useEnergyData` resta
	// sull'intera serie: alimenta i totali storici, che non sono grandezze «del
	// periodo».
	const [period, setPeriod] = useState<DashboardViewPeriod>('oggi');
	const { summary, isLoading: summaryLoading } = useDashboardSummary(orgId, period);
	// Carichi ottimizzati nel riquadro autoconsumo: il mese lo decide il server.
	const aiActionsCount = useAiActionsCount(orgId);
	const [metric, setMetric] = useState<MetricExplodeTarget | null>(null);

	// Un solo flag per tutto il pannello: card sbloccate una alla volta via via
	// che il proprio hook finisce dava un effetto "a scatti" poco leggibile.
	// Finché anche un solo dato del pannello manca, tutte le card restano in
	// skeleton insieme, e si sbloccano tutte insieme quando arriva l'ultimo.
	// Timing reale, senza floor minimo: vedi `useMinDuration` in
	// `@/hooks/use-min-duration` per allungare la visibilità quando serve.
	const panelLoading =
		configLoading ||
		summaryLoading ||
		membersLoading ||
		energyLoading;

	return (
		<>
			<DashboardPanelView
				config={config}
				members={members}
				energyTotals={energyTotals}
				summary={summary}
				showAiSuggestions={features.aiSuggestions === 'enabled'}
				aiActionsCount={aiActionsCount}
				period={period}
				onPeriodChange={setPeriod}
				onExplode={(metricId, kpiTotal) =>
					setMetric({ metricId, kpiTotal, orgId, ...explodeRange(period) })
				}
				loading={{
					kpis: panelLoading,
					topMembers: panelLoading,
					autoconsumo: panelLoading,
				}}
			/>
			<MetricExplode target={metric} onClose={() => setMetric(null)} />
		</>
	);
}
